<template>
  <div class="request-list">
    <!-- Error State -->
    <ErrorState v-if="error" :error-message="error" @retry="handleRetry" />

    <!-- Loading State -->
    <LoadingState v-else-if="loading" />

    <!-- Empty State -->
    <EmptyState
      v-else-if="!items.length"
      :empty-state-config="emptyStateConfig"
    />

    <!-- Content -->
    <TenderList
      v-else
      :loading="loading"
      :tenders-list="tendersList"
      :supplier-id="supplierId"
      @reload-tender-list="handleReloadTenderList"
      @agreement-click="handleAgreementClick"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import TenderList from "@/components/MyTenders/TenderList.vue";
import type { Tender } from "@/stores/tender/types";
import ErrorState from "./RequestListStates/ErrorState.vue";
import LoadingState from "./RequestListStates/LoadingState.vue";
import EmptyState from "./RequestListStates/EmptyState.vue";
import { useEmptyStateConfig } from "./composables/useEmptyStateConfig";

// Types
interface Props {
  loading: boolean;
  error?: string;
  items: Tender[];
  title: string;
  supplierId: number;
}

// Props with defaults
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  error: "",
  items: () => [],
});

// Emits
const emit = defineEmits(["retry", "reload-tender-list", "agreement-click"]);

// Composables
const emptyStateConfig = useEmptyStateConfig();

// Computed properties
const tendersList = computed(() => {
  if (!props.items.length) return [];

  return [
    {
      title: props.title,
      tenders: props.items,
    },
  ];
});

// Event handlers
const handleRetry = (): void => {
  emit("retry");
};

const handleReloadTenderList = (): void => {
  emit("reload-tender-list");
};

const handleAgreementClick = (priceRequestId: number | string): void => {
  emit("agreement-click", priceRequestId);
};
</script>

<style lang="scss" scoped>
.request-list {
  position: relative;
  margin-top: 1rem;
}
</style>
